import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
  type ReactNode,
} from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableSlotGroup,
  EditableToken,
  RichTextContent,
  useCompanyData,
  useContentToken,
  useEditMode,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import {
  AnimatePresence,
  motion,
  useScroll,
  useTransform,
  type MotionValue,
} from 'framer-motion';
import { ArrowLeft, ArrowRight, ExternalLink } from 'lucide-react';
import {
  FootprintMap,
  MAP_H,
  MAP_LAT_MAX,
  MAP_LAT_MIN,
  MAP_LNG_MAX,
  MAP_LNG_MIN,
  MAP_W,
  StateButton,
  type MapPoint,
} from './InvestorsFootprintMap';
import {
  InvestorContactForm,
  PortalButton,
  Spotlight,
  trackSpotlight,
} from './investorsSharedSections';
import {
  getCommunityPath,
  getCommunityProperties,
  getLatLng,
  normalizeAbbr,
} from './propertyUtils';

const leader1Def: SlotGroupDefinition = {
  label: 'Leader 1',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'Andrew Yam' },
    role: {
      label: 'Role',
      type: 'text',
      defaultValue: 'Sr. Managing Director, Global Head of Capital Markets',
    },
    dept: { label: 'Department', type: 'text', defaultValue: 'Capital Markets' },
    image: {
      label: 'Photo',
      type: 'image',
      defaultValue:
        'https://media.multihub.io/cdn-cgi/image/format=auto,w=1280/company/5695708787113984/01a0a172-2db3-753d-b7e3-6e33c1fef26d.webp',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Andrew Yam' },
    url: { label: 'Profile Page URL', type: 'url', defaultValue: '/team/andrew-yam' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const leader2Def: SlotGroupDefinition = {
  label: 'Leader 2',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'Jessica Wichser' },
    role: { label: 'Role', type: 'text', defaultValue: 'Senior Executive Portfolio Manager' },
    dept: { label: 'Department', type: 'text', defaultValue: 'Capital Markets' },
    image: {
      label: 'Photo',
      type: 'image',
      defaultValue: 'https://irp.cdn-website.com/dac7e7d5/dms3rep/multi/Jessica+Wichser+%281%29.png',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Jessica Wichser' },
    url: { label: 'Profile Page URL', type: 'url', defaultValue: '/team/jessica-wichser' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const leader3Def: SlotGroupDefinition = {
  label: 'Leader 3',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'Aharon Dinovitz' },
    role: { label: 'Role', type: 'text', defaultValue: 'Managing Director of Private Wealth' },
    dept: { label: 'Department', type: 'text', defaultValue: 'Capital Markets' },
    image: {
      label: 'Photo',
      type: 'image',
      defaultValue:
        'https://media.multihub.io/cdn-cgi/image/format=auto,w=1280/company/5695708787113984/01a0a195-2c1d-7618-8750-1de088b61157.webp',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Aharon Dinovitz' },
    url: { label: 'Profile Page URL', type: 'url', defaultValue: '/team/aharon-dinovitz' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const leader4Def: SlotGroupDefinition = {
  label: 'Leader 4',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'Tom Lubeck' },
    role: { label: 'Role', type: 'text', defaultValue: 'Director of Capital Markets' },
    dept: { label: 'Department', type: 'text', defaultValue: 'Capital Markets' },
    image: {
      label: 'Photo',
      type: 'image',
      defaultValue:
        'https://media.multihub.io/cdn-cgi/image/format=auto,w=1280/company/5695708787113984/01a0a195-def9-7780-850e-f0ace773d397.webp',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Tom Lubeck' },
    url: { label: 'Profile Page URL', type: 'url', defaultValue: '/team/tom-lubeck' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const leader5Def: SlotGroupDefinition = {
  label: 'Leader 5',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'Siara Rodriguez-Mirabel' },
    role: { label: 'Role', type: 'text', defaultValue: 'Vice President, Capital Markets' },
    dept: { label: 'Department', type: 'text', defaultValue: 'Capital Markets' },
    image: {
      label: 'Photo',
      type: 'image',
      defaultValue: 'https://irp.cdn-website.com/dac7e7d5/dms3rep/multi/Siara.png',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Siara Rodriguez-Mirabel' },
    url: { label: 'Profile Page URL', type: 'url', defaultValue: '/team/siara-rodriguez-mirabel' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const leader6Def: SlotGroupDefinition = {
  label: 'Leader 6',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'Ross Fayne' },
    role: { label: 'Role', type: 'text', defaultValue: 'Capital Markets' },
    dept: { label: 'Department', type: 'text', defaultValue: 'Capital Markets' },
    image: {
      label: 'Photo',
      type: 'image',
      defaultValue:
        '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Ross Fayne' },
    url: { label: 'Profile Page URL', type: 'url', defaultValue: '' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const leader7Def: SlotGroupDefinition = {
  label: 'Leader 7',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'Ali Serrhini' },
    role: { label: 'Role', type: 'text', defaultValue: 'Managing Director, Capital Markets' },
    dept: { label: 'Department', type: 'text', defaultValue: 'Capital Markets' },
    image: {
      label: 'Photo',
      type: 'image',
      defaultValue: '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Ali Serrhini' },
    url: { label: 'Profile Page URL', type: 'url', defaultValue: '/team/ali-serrhini' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const leader8Def: SlotGroupDefinition = {
  label: 'Leader 8',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'Greta Ivezaj' },
    role: { label: 'Role', type: 'text', defaultValue: 'Capital Markets' },
    dept: { label: 'Department', type: 'text', defaultValue: 'Capital Markets' },
    image: {
      label: 'Photo',
      type: 'image',
      defaultValue: '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Greta Ivezaj' },
    url: { label: 'Profile Page URL', type: 'url', defaultValue: '/team/greta-ivezaj' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const teamCtaDef: SlotGroupDefinition = {
  label: 'Team CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/team' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Meet the Capital Markets Team' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const contactCtaDef: SlotGroupDefinition = {
  label: 'Contact CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/contact-us' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Contact Our Team' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const loginCtaDef: SlotGroupDefinition = {
  label: 'Investor Login',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: 'https://www.alapts.com/investors',
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Investor Login' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const timelinePortalDef: SlotGroupDefinition = {
  label: 'Timeline Portal Button',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.alapts.com/investors' },
    label: { label: 'Button Text', type: 'text', defaultValue: 'Investor Portal' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const partnersPortalDef: SlotGroupDefinition = {
  label: 'Partners Portal Button',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.alapts.com/investors' },
    label: { label: 'Button Text', type: 'text', defaultValue: 'Investor Portal' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const milestone1Def: SlotGroupDefinition = {
  label: 'Milestone 1',
  fields: {
    year: { label: 'Year', type: 'text', defaultValue: '1996' },
    title: { label: 'Title', type: 'text', defaultValue: 'Landmark Residential Formed' },
    body: {
      label: 'Body',
      type: 'text',
      defaultValue: 'Founded by Joe Lubeck in Tampa, FL.',
    },
  },
};

const milestone2Def: SlotGroupDefinition = {
  label: 'Milestone 2',
  fields: {
    year: { label: 'Year', type: 'text', defaultValue: '2008' },
    title: { label: 'Title', type: 'text', defaultValue: 'Partnership with ELCO Holdings (Electra)' },
    body: {
      label: 'Body',
      type: 'text',
      defaultValue: 'ELCO Landmark Residential Holdings formed.',
    },
  },
};

const milestone3Def: SlotGroupDefinition = {
  label: 'Milestone 3',
  fields: {
    year: { label: 'Year', type: 'text', defaultValue: '2013' },
    title: { label: 'Title', type: 'text', defaultValue: 'Landmark Apartment Trust Formed' },
    body: {
      label: 'Body',
      type: 'text',
      defaultValue: 'Acquisition of Apartment Trust of America, a public non-traded REIT.',
    },
  },
};

const milestone4Def: SlotGroupDefinition = {
  label: 'Milestone 4',
  fields: {
    year: { label: 'Year', type: 'text', defaultValue: '2015' },
    title: { label: 'Title', type: 'text', defaultValue: 'Sale of Landmark Apartment Trust' },
    body: {
      label: 'Body',
      type: 'text',
      defaultValue:
        '24,000 units sold to Monument Partners (Starwood & Milestone) for $1.9B.',
    },
  },
};

const milestone5Def: SlotGroupDefinition = {
  label: 'Milestone 5',
  fields: {
    year: { label: 'Year', type: 'text', defaultValue: '2016' },
    title: { label: 'Title', type: 'text', defaultValue: 'Rebranded as American Landmark Apartments' },
    body: {
      label: 'Body',
      type: 'text',
      defaultValue: 'Senior Management Team continues under new branding.',
    },
  },
};

const milestone6Def: SlotGroupDefinition = {
  label: 'Milestone 6',
  fields: {
    year: { label: 'Year', type: 'text', defaultValue: '2017' },
    title: { label: 'Title', type: 'text', defaultValue: 'Launches Fund I' },
    body: {
      label: 'Body',
      type: 'text',
      defaultValue: 'Closed $214M in commitments.',
    },
  },
};

const milestone7Def: SlotGroupDefinition = {
  label: 'Milestone 7',
  fields: {
    year: { label: 'Year', type: 'text', defaultValue: '2018' },
    title: { label: 'Title', type: 'text', defaultValue: 'Launches Fund II' },
    body: {
      label: 'Body',
      type: 'text',
      defaultValue: 'Closed $462M in commitments.',
    },
  },
};

const milestone8Def: SlotGroupDefinition = {
  label: 'Milestone 8',
  fields: {
    year: { label: 'Year', type: 'text', defaultValue: '2019' },
    title: { label: 'Title', type: 'text', defaultValue: 'Launches Fund III' },
    body: {
      label: 'Body',
      type: 'text',
      defaultValue: 'Closed $981M in commitments.',
    },
  },
};

const milestone9Def: SlotGroupDefinition = {
  label: 'Milestone 9',
  fields: {
    year: { label: 'Year', type: 'text', defaultValue: '2022' },
    title: {
      label: 'Title',
      type: 'text',
      defaultValue: 'Launches Fund IV + Fund I Fully Realized',
    },
    body: {
      label: 'Body',
      type: 'text',
      defaultValue:
        'Fund IV closed $1.04B. Fund I liquidated Q3 2022 at 22.8% Net IRR, 2.3x Net Multiple.',
    },
  },
};

const milestone10Def: SlotGroupDefinition = {
  label: 'Milestone 10',
  fields: {
    year: { label: 'Year', type: 'text', defaultValue: '2026' },
    title: { label: 'Title', type: 'text', defaultValue: 'Launches Fund V' },
    body: {
      label: 'Body',
      type: 'text',
      defaultValue: '$1.1B target. ~$479M committed as of August 2026.¹',
    },
  },
};

const choosePersonDef: SlotGroupDefinition = {
  label: 'Why Choose Us Person',
  fields: {
    image: {
      label: 'Photo',
      type: 'image',
      defaultValue:
        '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Joe Lubeck' },
    name: { label: 'Name', type: 'text', defaultValue: 'Joe Lubeck' },
    role: {
      label: 'Role',
      type: 'text',
      defaultValue: 'Founder & Chief Executive Officer',
    },
  },
};

const CHOOSE_CARD_LAYOUT = [
  'lg:self-end',
  'lg:self-start lg:ml-[12%]',
  'lg:self-end lg:mr-[12%]',
  'lg:self-start',
] as const;

const projectsServicesCtaDef: SlotGroupDefinition = {
  label: 'View Services CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/strategy' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'View Services' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const projectsAboutCtaDef: SlotGroupDefinition = {
  label: 'Learn About Us CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/about-us' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Learn About Us' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const project1Def: SlotGroupDefinition = {
  label: 'Featured Project 1',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://media.multihub.io/cdn-cgi/image/format=auto,w=1280/property/5753550520975360/019d02f5-7ce9-72af-ac2a-eb4a690dfed8.webp',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'The Jaxon' },
    title: { label: 'Title', type: 'text', defaultValue: 'The Jaxon' },
    location: { label: 'Location', type: 'text', defaultValue: 'Houston, TX' },
    status: { label: 'Badge', type: 'text', defaultValue: 'Featured' },
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/projects/the-jaxon',
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const project2Def: SlotGroupDefinition = {
  label: 'Featured Project 2',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://media.multihub.io/cdn-cgi/image/format=auto,w=1280/property/5738013569056768/019d02f3-564c-703f-b796-e1e38f3d6a30.webp',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'The Legend' },
    title: { label: 'Title', type: 'text', defaultValue: 'The Legend' },
    location: { label: 'Location', type: 'text', defaultValue: 'San Antonio, TX' },
    status: { label: 'Badge', type: 'text', defaultValue: 'Featured' },
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/projects/the-legend',
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const project3Def: SlotGroupDefinition = {
  label: 'Featured Project 3',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://media.multihub.io/cdn-cgi/image/format=auto,w=1280/property/6737460151194273/019d02f6-11ee-7648-9b11-c802760a56d7.webp',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'The Pointe on Westshore' },
    title: { label: 'Title', type: 'text', defaultValue: 'The Pointe on Westshore' },
    location: { label: 'Location', type: 'text', defaultValue: 'Tampa, FL' },
    status: { label: 'Badge', type: 'text', defaultValue: 'Featured' },
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/projects/the-pointe-on-westshore',
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const project4Def: SlotGroupDefinition = {
  label: 'Featured Project 4',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://media.multihub.io/cdn-cgi/image/format=auto,w=1280/property/5694569974857728/019d02f1-aa08-733b-97b4-1a404d227eee.webp',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'City Harbor' },
    title: { label: 'Title', type: 'text', defaultValue: 'City Harbor' },
    location: { label: 'Location', type: 'text', defaultValue: 'Melbourne, FL' },
    status: { label: 'Badge', type: 'text', defaultValue: 'Featured' },
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/projects/city-harbor',
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const project5Def: SlotGroupDefinition = {
  label: 'Featured Project 5',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://media.multihub.io/cdn-cgi/image/format=auto,w=1280/property/5754272234864640/019d02f4-3eba-731d-bbbd-a7d533f8c7e6.webp',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Broadway Chapter' },
    title: { label: 'Title', type: 'text', defaultValue: 'Broadway Chapter' },
    location: { label: 'Location', type: 'text', defaultValue: 'Fort Worth, TX' },
    status: { label: 'Badge', type: 'text', defaultValue: 'Featured' },
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/projects/broadway-chapter',
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const state1Def: SlotGroupDefinition = {
  label: 'State 1',
  fields: {
    name: { label: 'State', type: 'text', defaultValue: 'Texas' },
    abbr: { label: 'State Code (links to the map)', type: 'text', defaultValue: 'TX' },
    region: { label: 'Region', type: 'text', defaultValue: 'Texas Triangle' },
    markets: {
      label: 'Key Markets',
      type: 'text',
      defaultValue: 'Dallas · Fort Worth · Houston · San Antonio · Austin',
    },
  },
};

const state2Def: SlotGroupDefinition = {
  label: 'State 2',
  fields: {
    name: { label: 'State', type: 'text', defaultValue: 'Florida' },
    abbr: { label: 'State Code (links to the map)', type: 'text', defaultValue: 'FL' },
    region: { label: 'Region', type: 'text', defaultValue: 'Florida' },
    markets: {
      label: 'Key Markets',
      type: 'text',
      defaultValue:
        'Fort Lauderdale · Jacksonville · Miami · Orlando · Palm Bay · Port St. Lucie · Tampa · West Palm Beach',
    },
  },
};

const state3Def: SlotGroupDefinition = {
  label: 'State 3',
  fields: {
    name: { label: 'State', type: 'text', defaultValue: 'North Carolina' },
    abbr: { label: 'State Code (links to the map)', type: 'text', defaultValue: 'NC' },
    region: { label: 'Region', type: 'text', defaultValue: 'Carolinas' },
    markets: { label: 'Key Markets', type: 'text', defaultValue: 'Charlotte · Raleigh/Durham' },
  },
};

const state4Def: SlotGroupDefinition = {
  label: 'State 4',
  fields: {
    name: { label: 'State', type: 'text', defaultValue: 'Georgia' },
    abbr: { label: 'State Code (links to the map)', type: 'text', defaultValue: 'GA' },
    region: { label: 'Region', type: 'text', defaultValue: 'Southeast' },
    markets: { label: 'Key Markets', type: 'text', defaultValue: 'Atlanta' },
  },
};

const state5Def: SlotGroupDefinition = {
  label: 'State 5',
  fields: {
    name: { label: 'State', type: 'text', defaultValue: 'South Carolina' },
    abbr: { label: 'State Code (links to the map)', type: 'text', defaultValue: 'SC' },
    region: { label: 'Region', type: 'text', defaultValue: 'Carolinas' },
    markets: { label: 'Key Markets', type: 'text', defaultValue: 'Charleston' },
  },
};

const state6Def: SlotGroupDefinition = {
  label: 'State 6',
  fields: {
    name: { label: 'State', type: 'text', defaultValue: 'Tennessee' },
    abbr: { label: 'State Code (links to the map)', type: 'text', defaultValue: 'TN' },
    region: { label: 'Region', type: 'text', defaultValue: 'Southeast' },
    markets: { label: 'Key Markets', type: 'text', defaultValue: 'Nashville' },
  },
};

const state7Def: SlotGroupDefinition = {
  label: 'State 7',
  fields: {
    name: { label: 'State', type: 'text', defaultValue: 'Virginia' },
    abbr: { label: 'State Code (links to the map)', type: 'text', defaultValue: 'VA' },
    region: { label: 'Region', type: 'text', defaultValue: 'Mid-Atlantic' },
    markets: { label: 'Key Markets', type: 'text', defaultValue: 'Richmond' },
  },
};

const state8Def: SlotGroupDefinition = {
  label: 'State 8',
  fields: {
    name: { label: 'State', type: 'text', defaultValue: 'Arizona' },
    abbr: { label: 'State Code (links to the map)', type: 'text', defaultValue: 'AZ' },
    region: { label: 'Region', type: 'text', defaultValue: 'Southwest' },
    markets: { label: 'Key Markets', type: 'text', defaultValue: 'Phoenix' },
  },
};

const FOOTPRINT_EYEBROW_CLASS =
  'font-heading text-sm font-semibold uppercase tracking-[0.14em]';
const FOOTPRINT_H2_CLASS =
  'font-heading text-[clamp(32px,4vw,58px)] font-semibold leading-[1.04] tracking-[-0.03em]';

const award1Def: SlotGroupDefinition = {
  label: 'Award Logo 1',
  fields: {
    image: { label: 'Logo', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'NAA Excellence Awards 2025 Winner' },
  },
};

const award2Def: SlotGroupDefinition = {
  label: 'Award Logo 2',
  fields: {
    image: { label: 'Logo', type: 'image', defaultValue: '' },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Top Work Places 2026 – jacksonville.com, The Florida Times-Union',
    },
  },
};

const award3Def: SlotGroupDefinition = {
  label: 'Award Logo 3',
  fields: {
    image: { label: 'Logo', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: '2026 NMHC 50 Top Owners' },
  },
};

const award4Def: SlotGroupDefinition = {
  label: 'Award Logo 4',
  fields: {
    image: { label: 'Logo', type: 'image', defaultValue: '' },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'J Turner Research ORA Power Rankings Elite 1% 2023',
    },
  },
};

const award5Def: SlotGroupDefinition = {
  label: 'Award Logo 5',
  fields: {
    image: { label: 'Logo', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: '2026 NMHC 50 Top Managers' },
  },
};

const award6Def: SlotGroupDefinition = {
  label: 'Award Logo 6',
  fields: {
    image: { label: 'Logo', type: 'image', defaultValue: '' },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Top Work Places 2023–2025 – Houston Chronicle',
    },
  },
};

const award7Def: SlotGroupDefinition = {
  label: 'Award Logo 7',
  fields: {
    image: { label: 'Logo', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'USA Today Top Work Places 2025–2026' },
  },
};

const award8Def: SlotGroupDefinition = {
  label: 'Award Logo 8',
  fields: {
    image: { label: 'Logo', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Award badge' },
  },
};

const award9Def: SlotGroupDefinition = {
  label: 'Award Logo 9',
  fields: {
    image: { label: 'Logo', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Award badge' },
  },
};

const award10Def: SlotGroupDefinition = {
  label: 'Award Logo 10',
  fields: {
    image: { label: 'Logo', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Award badge' },
  },
};

const award11Def: SlotGroupDefinition = {
  label: 'Award Logo 11',
  fields: {
    image: { label: 'Logo', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Award badge' },
  },
};

const award12Def: SlotGroupDefinition = {
  label: 'Award Logo 12',
  fields: {
    image: { label: 'Logo', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Award badge' },
  },
};

function AwardLogo({
  image,
  alt,
  knockoutWhite = false,
}: {
  image?: string | null;
  alt?: string | null;
  knockoutWhite?: boolean;
}) {
  return (
    <div
      className={`flex h-[140px] w-[200px] shrink-0 items-center justify-center${knockoutWhite ? ' bg-[rgb(var(--ala-surface))]' : ''}`}
    >
      {image ? (
        <VibesitesImage
          url={image}
          alt={alt ?? 'Award badge'}
          sizes="200px"
          className={`max-h-full max-w-full object-contain${knockoutWhite ? ' mix-blend-multiply' : ''}`}
        />
      ) : (
        <span className="flex h-full w-full items-center justify-center rounded-lg border border-dashed border-[rgb(var(--ala-dark))]/25 font-heading text-xs uppercase tracking-[0.12em] text-[rgb(var(--ala-dark))]/45">
          Add logo
        </span>
      )}
    </div>
  );
}

function EyebrowDot({ light = false }: { light?: boolean }) {
  return (
    <span
      className={`h-2 w-2 shrink-0 rounded-[3px] ${light ? 'bg-[rgb(var(--ala-secondary))]' : 'bg-[rgb(var(--ala-secondary))]'}`}
      aria-hidden
    />
  );
}

type SlotData = Record<string, string | null | undefined>;

const TIMELINE_EYEBROW_CLASS = 'font-heading text-sm font-semibold uppercase tracking-[0.14em]';
const TIMELINE_H2_CLASS =
  'font-heading text-[clamp(32px,4vw,58px)] font-semibold leading-[1.04] tracking-[-0.03em]';

function TimelineEyebrow({ children }: { children: ReactNode }) {
  return (
    <div className="mb-5 flex items-center gap-2">
      <span className="h-2 w-2 shrink-0 rounded-[3px] bg-[rgb(var(--ala-secondary))]" aria-hidden />
      {children}
    </div>
  );
}

function TimelinePortalButton({ data }: { data: SlotData }) {
  return (
    <a
      href={data.url || 'https://www.alapts.com/investors'}
      target={data._target || '_blank'}
      rel="noopener noreferrer"
      className="group inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-[rgb(var(--ala-on-primary))]/40 px-7 py-4 font-heading text-sm font-semibold uppercase tracking-wide text-[rgb(var(--ala-on-primary))] transition-all duration-300 hover:-translate-y-0.5 hover:border-[rgb(var(--ala-secondary))] hover:bg-[rgb(var(--ala-secondary))]/10"
    >
      {data.label || 'Investor Portal'}
      <ExternalLink className="h-4 w-4 transition-transform group-hover:rotate-12" aria-hidden />
    </a>
  );
}

function Timeline({
  milestones,
  renderYearButton,
}: {
  milestones: SlotData[];
  renderYearButton: (index: number, button: ReactNode) => ReactNode;
}) {
  const [active, setActive] = useState(0);
  const current = milestones[active] || {};
  const last = milestones.length - 1;
  const go = (i: number) => setActive(Math.min(Math.max(i, 0), last));

  const railInset = `calc(100% / ${milestones.length * 2})`;
  const trackMinWidth = Math.max(640, milestones.length * 88);

  return (
    <div>
      <div className="mb-12 hidden pb-8 pt-4 md:block">
        <div className="relative md:min-w-0" style={{ minWidth: trackMinWidth }}>
          <div
            className="absolute top-[11px] h-[2px] bg-[rgb(var(--ala-on-primary))]/10"
            style={{ left: railInset, right: railInset }}
          >
            <div
              className="h-full bg-[rgb(var(--ala-secondary))] transition-[width] duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
              style={{ width: `${last > 0 ? (active / last) * 100 : 100}%` }}
            />
          </div>
          <div className="relative grid" style={{ gridTemplateColumns: `repeat(${milestones.length}, minmax(0, 1fr))` }}>
            {milestones.map((m, i) =>
              renderYearButton(
                i,
                <button
                  type="button"
                  onClick={() => go(i)}
                  aria-current={i === active ? 'step' : undefined}
                  aria-label={m.year ? `Milestone ${m.year}` : `Milestone ${i + 1}`}
                  className="group relative z-[1] flex w-full flex-col items-center gap-3"
                >
                  <span
                    className={`h-4 w-4 shrink-0 rounded-full border-2 transition-all duration-500 ${
                      i <= active
                        ? 'border-[rgb(var(--ala-secondary))] bg-[rgb(var(--ala-secondary))] shadow-[0_0_0_6px_rgba(84,158,255,0.2)]'
                        : 'border-[rgb(var(--ala-on-primary))]/30 bg-[rgb(var(--ala-dark))] group-hover:border-[rgb(var(--ala-on-primary))]/70'
                    } ${i === active ? 'scale-125' : ''}`}
                  />
                  <span
                    className={`whitespace-nowrap font-heading text-[15px] font-semibold leading-none tabular-nums tracking-[-0.02em] transition-colors md:text-[17px] ${
                      i === active
                        ? 'text-white'
                        : 'text-white/55 group-hover:text-white/85'
                    }`}
                  >
                    {m.year || '—'}
                  </span>
                </button>,
              ),
            )}
          </div>
        </div>
      </div>

      <div className="grid gap-8 rounded-3xl border border-[rgb(var(--ala-on-primary))]/10 bg-[rgb(var(--ala-on-primary))]/[0.03] p-7 md:p-10 lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)] lg:items-center">
        <AnimatePresence mode="wait" initial={false}>
          <motion.span
            key={`year-${active}`}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -24 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="block font-heading text-[clamp(88px,13vw,190px)] font-bold leading-[0.85] tracking-[-0.05em] text-[rgb(var(--ala-secondary))]"
          >
            {current.year}
          </motion.span>
        </AnimatePresence>
        <div className="flex flex-col gap-6">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={`copy-${active}`}
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -24 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="flex min-h-[150px] flex-col gap-3"
            >
              <h3 className="font-heading text-[clamp(24px,2.4vw,34px)] font-semibold leading-tight text-[rgb(var(--ala-on-primary))]">
                {current.title}
              </h3>
              <p className="max-w-[560px] font-body text-lg leading-relaxed text-[rgb(var(--ala-on-primary))]/70">
                {current.body}
              </p>
            </motion.div>
          </AnimatePresence>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => go(active - 1)}
              disabled={active === 0}
              aria-label="Previous milestone"
              className="flex h-11 w-11 items-center justify-center rounded-full border border-[rgb(var(--ala-on-primary))]/25 text-[rgb(var(--ala-on-primary))] transition-colors hover:bg-[rgb(var(--ala-on-primary))]/10 disabled:opacity-30"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => go(active + 1)}
              disabled={active === last}
              aria-label="Next milestone"
              className="flex h-11 w-11 items-center justify-center rounded-full border border-[rgb(var(--ala-on-primary))]/25 text-[rgb(var(--ala-on-primary))] transition-colors hover:bg-[rgb(var(--ala-on-primary))]/10 disabled:opacity-30"
            >
              <ArrowRight className="h-4 w-4" />
            </button>
            <span className="ml-2 font-heading text-sm tabular-nums text-[rgb(var(--ala-on-primary))]/50">
              {String(active + 1).padStart(2, '0')} / {String(milestones.length).padStart(2, '0')}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function ScrollRevealCard({
  progress,
  index,
  total,
  className,
  children,
}: {
  progress: MotionValue<number>;
  index: number;
  total: number;
  className?: string;
  children: ReactNode;
}) {
  const start = index / total;
  const end = start + 1 / total;
  const y = useTransform(progress, [start, end], [80, 0]);
  const opacity = useTransform(progress, [start, end], [0, 1]);
  return (
    <motion.div style={{ y, opacity }} className={className}>
      {children}
    </motion.div>
  );
}

function LeaderCard({
  groupPath,
  definition,
  fillCircleCrop = false,
}: {
  groupPath: string;
  definition: SlotGroupDefinition;
  /** Zoom circular cutout headshots so they fill the rectangular portrait frame. */
  fillCircleCrop?: boolean;
}) {
  const data = useSlotGroup(groupPath, definition);
  const imageUrl = data.image || '';
  const profileUrl = data.url || '';
  const PhotoFrame = profileUrl ? 'a' : 'div';
  const photoLinkProps = profileUrl
    ? {
        href: profileUrl,
        target: data._target || '_self',
        'aria-label': `View ${data.name || 'leader'} profile`,
      }
    : {};
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="group relative flex h-full w-full flex-col overflow-hidden rounded-xl bg-[rgb(var(--ala-background))] shadow-sm transition-shadow duration-300 hover:shadow-2xl"
    >
      <PhotoFrame
        {...photoLinkProps}
        className="relative block aspect-[3/4] w-full shrink-0 overflow-hidden bg-[#0A1626]"
      >
        {imageUrl ? (
          <VibesitesImage
            url={imageUrl}
            alt={data.alt || data.name || ''}
            sizes="(max-width: 1024px) 50vw, 240px"
            className={
              fillCircleCrop
                ? 'absolute inset-0 h-full w-full scale-[1.42] object-cover object-[center_22%] transition-transform duration-700 ease-out group-hover:scale-[1.5]'
                : 'absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105'
            }
          />
        ) : null}
        <div className="absolute inset-0 bg-gradient-to-br from-[rgb(var(--ala-dark))] to-[#1e3a5f] opacity-0 mix-blend-overlay transition-opacity duration-500 group-hover:opacity-20" />
      </PhotoFrame>
      <div className="flex flex-1 flex-col p-3 text-left sm:p-4">
        <p className="mb-1 font-heading text-[10px] uppercase tracking-[0.12em] text-[rgb(var(--ala-text-muted))] sm:text-[11px] sm:tracking-[0.14em]">
          {data.dept}
        </p>
        <h3 className="font-heading text-base font-medium leading-tight text-[rgb(var(--ala-text))] sm:text-lg">{data.name}</h3>
        <p className="mt-1 font-body text-xs text-[rgb(var(--ala-text-muted))] sm:text-sm">{data.role}</p>
      </div>
    </motion.div>
  );
}

function FeaturedProjectCard({
  groupPath,
  definition,
  suppressClickRef,
}: {
  groupPath: string;
  definition: SlotGroupDefinition;
  suppressClickRef: MutableRefObject<boolean>;
}) {
  const data = useSlotGroup(groupPath, definition);

  return (
    <a
      href={(data.url || definition.fields.url?.defaultValue || '/investors') as string}
      target={data._target || '_self'}
      draggable={false}
      onClick={(event) => {
        if (suppressClickRef.current) {
          event.preventDefault();
          event.stopPropagation();
        }
      }}
      className="group relative block min-h-[500px] w-full cursor-pointer overflow-clip rounded-lg md:h-[600px]"
    >
      <VibesitesImage
        url={data.image || ''}
        alt={data.alt || data.title || ''}
        sizes="(min-width: 768px) 662px, 85vw"
        className="pointer-events-none absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
      />
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,_rgb(var(--ala-dark)/0)_0%,_rgb(var(--ala-dark)/0.9)_100%)] transition-opacity duration-500 group-hover:opacity-0" />
      <div className="pointer-events-none absolute inset-0 bg-[rgb(var(--ala-dark))]/80 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
      <div className="pointer-events-none absolute inset-0 z-10 flex flex-col justify-end p-6 md:p-8">
        {data.status ? (
          <div className="absolute left-6 top-6 flex items-center gap-2 rounded-sm bg-black/20 px-3 py-1 backdrop-blur-[5px] transition-opacity duration-300 group-hover:opacity-0 md:left-8 md:top-8">
            <span className="font-heading text-sm font-normal text-[rgb(var(--ala-on-primary))]">
              {data.status}
            </span>
          </div>
        ) : null}
        <div className="flex w-full flex-col items-start">
          <p className="mb-1 font-heading text-lg font-normal text-white">
            {data.location}
          </p>
          <h3 className="font-heading text-[clamp(20px,2.5vw,36px)] font-medium leading-[1.19] tracking-[-0.019em] text-[rgb(var(--ala-on-primary))]">
            {data.title}
          </h3>
          <div className="mt-0 max-h-0 w-full overflow-hidden px-2 opacity-0 transition-all duration-500 ease-out group-hover:mt-6 group-hover:max-h-20 group-hover:opacity-100">
            <span className="flex w-full items-center justify-center gap-2 rounded-lg border border-[rgb(var(--ala-on-primary))]/50 px-6 py-3 font-heading text-sm font-semibold uppercase tracking-[-0.3px] text-[rgb(var(--ala-on-primary))] transition-colors group-hover:bg-[rgb(var(--ala-on-primary))]/10">
              View Details
              <ArrowRight className="h-4 w-4" />
            </span>
          </div>
        </div>
      </div>
    </a>
  );
}

function FootprintOverviewSection() {
  const companyData = useCompanyData();
  const state1 = useSlotGroup('investorscontent.state_1', state1Def);
  const state2 = useSlotGroup('investorscontent.state_2', state2Def);
  const state3 = useSlotGroup('investorscontent.state_3', state3Def);
  const state4 = useSlotGroup('investorscontent.state_4', state4Def);
  const state5 = useSlotGroup('investorscontent.state_5', state5Def);
  const state6 = useSlotGroup('investorscontent.state_6', state6Def);
  const state7 = useSlotGroup('investorscontent.state_7', state7Def);
  const state8 = useSlotGroup('investorscontent.state_8', state8Def);

  const [selectedState, setSelectedState] = useState<string | null>(null);
  const [previewState, setPreviewState] = useState<string | null>(null);

  const mapPoints = useMemo(() => {
    const points: MapPoint[] = [];
    for (const prop of getCommunityProperties(companyData)) {
      const ll = getLatLng(prop);
      if (!ll || !ll.lat || !ll.lng) continue;
      if (ll.lng < MAP_LNG_MIN || ll.lng > MAP_LNG_MAX || ll.lat < MAP_LAT_MIN || ll.lat > MAP_LAT_MAX)
        continue;
      points.push({
        id: String(prop.multihub_property_id || prop.property_slug || points.length),
        name: String(prop.property_name || ''),
        city: String(prop.property_address_city || ''),
        abbr: normalizeAbbr(String(prop.property_address_state || '').trim()),
        href: getCommunityPath(prop),
        x: ((ll.lng - MAP_LNG_MIN) / (MAP_LNG_MAX - MAP_LNG_MIN)) * MAP_W,
        y: ((MAP_LAT_MAX - ll.lat) / (MAP_LAT_MAX - MAP_LAT_MIN)) * MAP_H,
      });
    }
    return points;
  }, [companyData]);

  const communityCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const p of mapPoints) counts[p.abbr] = (counts[p.abbr] || 0) + 1;
    return counts;
  }, [mapPoints]);

  const mapFocus = previewState ?? selectedState;
  const abbrOf = (data: SlotData) => (data.abbr || '').trim().toUpperCase();
  const stateEntries = [
    { key: 'state_1', data: state1, def: state1Def },
    { key: 'state_2', data: state2, def: state2Def },
    { key: 'state_3', data: state3, def: state3Def },
    { key: 'state_4', data: state4, def: state4Def },
    { key: 'state_5', data: state5, def: state5Def },
    { key: 'state_6', data: state6, def: state6Def },
    { key: 'state_7', data: state7, def: state7Def },
    { key: 'state_8', data: state8, def: state8Def },
  ] as const;
  const stateProps = (data: SlotData) => {
    const abbr = abbrOf(data);
    return {
      data,
      count: communityCounts[abbr] || 0,
      active: mapFocus === abbr,
      onSelect: () => setSelectedState((cur) => (cur === abbr ? null : abbr)),
      onPreview: (on: boolean) => setPreviewState(on ? abbr : null),
    };
  };

  const stateScrollRef = useRef<HTMLDivElement>(null);
  const [stateScrollProgress, setStateScrollProgress] = useState(0);
  const updateStateScrollProgress = useCallback(() => {
    const el = stateScrollRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setStateScrollProgress(max > 0 ? (el.scrollLeft / max) * 100 : 0);
  }, []);

  useEffect(() => {
    const el = stateScrollRef.current;
    if (!el) return;
    updateStateScrollProgress();
    el.addEventListener('scroll', updateStateScrollProgress, { passive: true });
    window.addEventListener('resize', updateStateScrollProgress);
    return () => {
      el.removeEventListener('scroll', updateStateScrollProgress);
      window.removeEventListener('resize', updateStateScrollProgress);
    };
  }, [updateStateScrollProgress]);

  return (
    <section className="w-full bg-[rgb(var(--ala-surface))] py-20 md:py-28">
      <div className="mx-auto max-w-[1440px] px-6 md:px-[clamp(16px,3.3vw,48px)]">
        <div className="grid gap-8 lg:grid-cols-[minmax(0,6fr)_minmax(0,6fr)] lg:items-end">
          <div>
            <TimelineEyebrow>
              <RichTextContent
                path="investorscontent.footprint_eyebrow"
                label="Footprint Eyebrow"
                as="span"
                className={`${FOOTPRINT_EYEBROW_CLASS} text-[rgb(var(--ala-text-muted))]`}
              />
            </TimelineEyebrow>
            <RichTextContent
              path="investorscontent.footprint_headline"
              label="Footprint Headline"
              as="h2"
              className={`${FOOTPRINT_H2_CLASS} text-[rgb(var(--ala-text))]`}
            />
          </div>
          <RichTextContent
            path="investorscontent.footprint_body"
            label="Footprint Body"
            as="p"
            className="max-w-[620px] font-body text-lg leading-relaxed text-[rgb(var(--ala-text-muted))] lg:justify-self-start"
          />
        </div>

        <div className="mt-12 rounded-3xl bg-[rgb(var(--ala-primary))] p-5 md:p-8">
          <div className="grid gap-6 lg:grid-cols-[minmax(0,8fr)_minmax(0,4fr)] lg:items-center lg:gap-8">
            <div className="flex min-w-0 flex-col">
              <div className="mb-4 flex items-center justify-between gap-4">
                <RichTextContent
                  path="investorscontent.map_hint"
                  label="Map Hint"
                  as="span"
                  className="font-heading text-xs font-semibold uppercase tracking-[0.14em] text-[rgb(var(--ala-on-primary))]/50"
                />
                {selectedState ? (
                  <button
                    type="button"
                    onClick={() => setSelectedState(null)}
                    className="font-heading text-xs font-semibold uppercase tracking-[0.14em] text-[rgb(var(--ala-secondary))] hover:text-[rgb(var(--ala-on-primary))]"
                  >
                    Show all
                  </button>
                ) : null}
              </div>
              <div className="flex flex-1 items-center">
                <FootprintMap points={mapPoints} focus={mapFocus} />
              </div>
            </div>

            <div className="flex min-w-0 flex-col gap-3">
              {/* Mobile: native dropdown — most reliable on touch devices */}
              <label className="flex flex-col gap-2 lg:hidden">
                <span className="font-heading text-xs font-semibold uppercase tracking-[0.14em] text-[rgb(var(--ala-on-primary))]/50">
                  Select a state
                </span>
                <select
                  value={selectedState ?? ''}
                  onChange={(e) => setSelectedState(e.target.value || null)}
                  className="h-12 w-full appearance-none rounded-xl border border-[rgb(var(--ala-on-primary))]/20 bg-[rgb(var(--ala-on-primary))]/10 px-4 pr-10 font-heading text-base font-semibold text-[rgb(var(--ala-on-primary))] outline-none focus:border-[rgb(var(--ala-secondary))]"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 1rem center',
                  }}
                >
                  <option value="">All states</option>
                  {stateEntries.map(({ key, data }) => {
                    const abbr = abbrOf(data);
                    const count = communityCounts[abbr] || 0;
                    return (
                      <option key={key} value={abbr}>
                        {data.name || abbr} ({count})
                      </option>
                    );
                  })}
                </select>
              </label>

              <div
                ref={stateScrollRef}
                className="flex gap-3 overflow-x-auto overscroll-x-contain touch-pan-x snap-x snap-mandatory pb-1 lg:grid lg:grid-cols-2 lg:gap-2 lg:overflow-visible lg:overscroll-auto lg:touch-auto lg:snap-none lg:pb-0"
              >
                <EditableSlotGroup
                  groupPath="investorscontent.state_1"
                  definition={state1Def}
                  className="w-[min(260px,75vw)] shrink-0 snap-center lg:w-auto lg:min-w-0"
                >
                  <StateButton {...stateProps(state1)} />
                </EditableSlotGroup>
                <EditableSlotGroup
                  groupPath="investorscontent.state_2"
                  definition={state2Def}
                  className="w-[min(260px,75vw)] shrink-0 snap-center lg:w-auto lg:min-w-0"
                >
                  <StateButton {...stateProps(state2)} />
                </EditableSlotGroup>
                <EditableSlotGroup
                  groupPath="investorscontent.state_3"
                  definition={state3Def}
                  className="w-[min(260px,75vw)] shrink-0 snap-center lg:w-auto lg:min-w-0"
                >
                  <StateButton {...stateProps(state3)} />
                </EditableSlotGroup>
                <EditableSlotGroup
                  groupPath="investorscontent.state_4"
                  definition={state4Def}
                  className="w-[min(260px,75vw)] shrink-0 snap-center lg:w-auto lg:min-w-0"
                >
                  <StateButton {...stateProps(state4)} />
                </EditableSlotGroup>
                <EditableSlotGroup
                  groupPath="investorscontent.state_5"
                  definition={state5Def}
                  className="w-[min(260px,75vw)] shrink-0 snap-center lg:w-auto lg:min-w-0"
                >
                  <StateButton {...stateProps(state5)} />
                </EditableSlotGroup>
                <EditableSlotGroup
                  groupPath="investorscontent.state_6"
                  definition={state6Def}
                  className="w-[min(260px,75vw)] shrink-0 snap-center lg:w-auto lg:min-w-0"
                >
                  <StateButton {...stateProps(state6)} />
                </EditableSlotGroup>
                <EditableSlotGroup
                  groupPath="investorscontent.state_7"
                  definition={state7Def}
                  className="w-[min(260px,75vw)] shrink-0 snap-center lg:w-auto lg:min-w-0"
                >
                  <StateButton {...stateProps(state7)} />
                </EditableSlotGroup>
                <EditableSlotGroup
                  groupPath="investorscontent.state_8"
                  definition={state8Def}
                  className="w-[min(260px,75vw)] shrink-0 snap-center lg:w-auto lg:min-w-0"
                >
                  <StateButton {...stateProps(state8)} />
                </EditableSlotGroup>
              </div>

              {/* Always-visible scroll track on mobile (native scrollbars often hide on iOS) */}
              <div className="lg:hidden">
                <div className="h-1.5 overflow-hidden rounded-full bg-[rgb(var(--ala-on-primary))]/15">
                  <div
                    className="h-full rounded-full bg-[rgb(var(--ala-secondary))] transition-[width] duration-150"
                    style={{ width: `${Math.max(stateScrollProgress, 8)}%` }}
                  />
                </div>
                <p className="mt-2 font-heading text-[11px] font-semibold uppercase tracking-[0.12em] text-[rgb(var(--ala-on-primary))]/45">
                  Swipe to browse states
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FeaturedProjectsSection() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef({
    active: false,
    pointerId: -1,
    startX: 0,
    startScroll: 0,
    moved: false,
  });
  const suppressClickRef = useRef(false);
  const [progress, setProgress] = useState(0);
  const [dragging, setDragging] = useState(false);
  const servicesCta = useSlotGroup('investorscontent.projects_services_cta', projectsServicesCtaDef);
  const aboutCta = useSlotGroup('investorscontent.projects_about_cta', projectsAboutCtaDef);

  const updateProgress = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    setProgress(maxScroll > 0 ? (el.scrollLeft / maxScroll) * 100 : 0);
  }, []);

  const scrollByPage = useCallback(
    (direction: -1 | 1) => {
      const el = scrollRef.current;
      if (!el) return;
      const amount = Math.max(el.clientWidth * 0.75, 280);
      el.scrollBy({ left: direction * amount, behavior: 'smooth' });
    },
    [],
  );

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const onScroll = () => updateProgress();
    const onResize = () => updateProgress();

    const onPointerDown = (event: PointerEvent) => {
      if (event.button !== 0) return;
      // Track potential drag, but do not capture yet — capturing on every
      // pointerdown retargets mouseup away from the card <a> and kills navigation.
      dragRef.current = {
        active: true,
        pointerId: event.pointerId,
        startX: event.clientX,
        startScroll: el.scrollLeft,
        moved: false,
      };
      suppressClickRef.current = false;
    };

    const onPointerMove = (event: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag.active || drag.pointerId !== event.pointerId) return;
      const delta = event.clientX - drag.startX;
      if (!drag.moved) {
        if (Math.abs(delta) <= 6) return;
        drag.moved = true;
        suppressClickRef.current = true;
        setDragging(true);
        el.setPointerCapture(event.pointerId);
      }
      el.scrollLeft = drag.startScroll - delta;
      updateProgress();
    };

    const endDrag = (event: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag.active || drag.pointerId !== event.pointerId) return;
      const wasDrag = drag.moved;
      drag.active = false;
      setDragging(false);
      if (el.hasPointerCapture(event.pointerId)) {
        el.releasePointerCapture(event.pointerId);
      }
      if (wasDrag) {
        // Keep suppress through the synthetic click that follows a drag.
        window.setTimeout(() => {
          suppressClickRef.current = false;
        }, 0);
      } else {
        suppressClickRef.current = false;
      }
    };

    const onWheel = (event: WheelEvent) => {
      if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;
      if (event.deltaY === 0) return;
      const maxScroll = el.scrollWidth - el.clientWidth;
      if (maxScroll <= 0) return;
      const atStart = el.scrollLeft <= 0 && event.deltaY < 0;
      const atEnd = el.scrollLeft >= maxScroll - 1 && event.deltaY > 0;
      if (atStart || atEnd) return;
      event.preventDefault();
      el.scrollLeft += event.deltaY;
      updateProgress();
    };

    updateProgress();
    el.addEventListener('scroll', onScroll, { passive: true });
    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('pointermove', onPointerMove);
    el.addEventListener('pointerup', endDrag);
    el.addEventListener('pointercancel', endDrag);
    el.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('resize', onResize);
    return () => {
      el.removeEventListener('scroll', onScroll);
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('pointermove', onPointerMove);
      el.removeEventListener('pointerup', endDrag);
      el.removeEventListener('pointercancel', endDrag);
      el.removeEventListener('wheel', onWheel);
      window.removeEventListener('resize', onResize);
    };
  }, [updateProgress]);

  return (
    <section className="w-full overflow-x-clip bg-[rgb(var(--ala-surface))] py-16 md:py-[clamp(30px,8.3vw,120px)] pl-6 md:pl-[clamp(16px,3.3vw,48px)]">
      <div className="mx-auto w-full max-w-[1440px]">
        <div className="mb-8 flex flex-col gap-6 pr-6 md:mb-12 md:flex-row md:items-end md:justify-between md:pr-[clamp(16px,3.3vw,48px)]">
          <RichTextContent
            path="investorscontent.projects_headline"
            label="Featured Projects Headline"
            as="h2"
            className="font-heading text-[clamp(29px,3.61vw,52px)] font-normal leading-[1.19] tracking-[-0.019em] text-[rgb(var(--ala-dark))]"
          />
          <div className="flex flex-wrap items-center gap-3">
            <EditableSlotGroup
              groupPath="investorscontent.projects_services_cta"
              definition={projectsServicesCtaDef}
            >
              <a
                href={servicesCta.url || '/strategy'}
                target={servicesCta._target || '_self'}
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-[rgb(var(--ala-secondary))] px-6 py-4 font-heading text-sm font-medium uppercase tracking-wide text-[rgb(var(--ala-on-secondary))] transition-colors hover:bg-[#4080d0]"
              >
                <span>{servicesCta.label || 'View Services'}</span>
                <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
              </a>
            </EditableSlotGroup>
            <EditableSlotGroup
              groupPath="investorscontent.projects_about_cta"
              definition={projectsAboutCtaDef}
            >
              <a
                href={aboutCta.url || '/about-us'}
                target={aboutCta._target || '_self'}
                className="group inline-flex items-center justify-center gap-2 rounded-full border border-[rgb(var(--ala-dark))]/40 px-6 py-4 font-heading text-sm font-medium uppercase tracking-wide text-[rgb(var(--ala-dark))] transition-colors hover:bg-[rgb(var(--ala-dark))]/5"
              >
                <span>{aboutCta.label || 'Learn About Us'}</span>
                <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
              </a>
            </EditableSlotGroup>
          </div>
        </div>

        <div
          ref={scrollRef}
          role="region"
          aria-label="Featured projects"
          className={`-ml-4 flex touch-pan-x snap-x snap-mandatory gap-0 overflow-x-auto overscroll-x-contain pb-2 [-ms-overflow-style:none] [scrollbar-width:none] md:-ml-8 [&::-webkit-scrollbar]:hidden ${
            dragging ? 'cursor-grabbing select-none' : 'cursor-grab'
          }`}
        >
          <div className="w-[85%] shrink-0 snap-start pl-4 md:w-[662px] md:pl-8">
            <EditableSlotGroup groupPath="investorscontent.project_1" definition={project1Def}>
              <FeaturedProjectCard
                groupPath="investorscontent.project_1"
                definition={project1Def}
                suppressClickRef={suppressClickRef}
              />
            </EditableSlotGroup>
          </div>
          <div className="w-[85%] shrink-0 snap-start pl-4 md:w-[662px] md:pl-8">
            <EditableSlotGroup groupPath="investorscontent.project_2" definition={project2Def}>
              <FeaturedProjectCard
                groupPath="investorscontent.project_2"
                definition={project2Def}
                suppressClickRef={suppressClickRef}
              />
            </EditableSlotGroup>
          </div>
          <div className="w-[85%] shrink-0 snap-start pl-4 md:w-[662px] md:pl-8">
            <EditableSlotGroup groupPath="investorscontent.project_3" definition={project3Def}>
              <FeaturedProjectCard
                groupPath="investorscontent.project_3"
                definition={project3Def}
                suppressClickRef={suppressClickRef}
              />
            </EditableSlotGroup>
          </div>
          <div className="w-[85%] shrink-0 snap-start pl-4 md:w-[662px] md:pl-8">
            <EditableSlotGroup groupPath="investorscontent.project_4" definition={project4Def}>
              <FeaturedProjectCard
                groupPath="investorscontent.project_4"
                definition={project4Def}
                suppressClickRef={suppressClickRef}
              />
            </EditableSlotGroup>
          </div>
          <div className="w-[85%] shrink-0 snap-start pl-4 md:w-[662px] md:pl-8">
            <EditableSlotGroup groupPath="investorscontent.project_5" definition={project5Def}>
              <FeaturedProjectCard
                groupPath="investorscontent.project_5"
                definition={project5Def}
                suppressClickRef={suppressClickRef}
              />
            </EditableSlotGroup>
          </div>
          <div className="w-6 shrink-0 md:w-12" aria-hidden />
        </div>

        <div className="mt-8 flex items-center gap-4 pr-6 md:pr-[clamp(16px,3.3vw,48px)]">
          <div className="h-[3px] min-w-0 flex-1 overflow-hidden rounded-full bg-[rgb(var(--ala-dark))]/10">
            <div
              className="h-full rounded-full bg-[rgb(var(--ala-secondary))] transition-[width] duration-150 ease-out"
              style={{ width: `${progress}%` }}
              aria-hidden
            />
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              aria-label="Previous projects"
              onClick={() => scrollByPage(-1)}
              disabled={progress <= 0}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-[rgb(var(--ala-dark))]/20 text-[rgb(var(--ala-dark))] transition-colors hover:bg-[rgb(var(--ala-dark))]/5 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              aria-label="Next projects"
              onClick={() => scrollByPage(1)}
              disabled={progress >= 99.5}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-[rgb(var(--ala-dark))]/20 text-[rgb(var(--ala-dark))] transition-colors hover:bg-[rgb(var(--ala-dark))]/5 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function ValueAddSpotlightSection() {
  return (
    <section className="relative w-full overflow-hidden bg-[rgb(var(--ala-dark))] py-20 md:py-28">
      <div
        className="pointer-events-none absolute -right-40 top-10 h-[520px] w-[520px] rounded-full bg-[rgb(var(--ala-secondary))]/10 blur-[140px]"
        aria-hidden
      />
      <div className="relative mx-auto max-w-[1440px] px-6 md:px-[clamp(16px,3.3vw,48px)]">
        <div className="grid gap-8 lg:grid-cols-[minmax(0,6fr)_minmax(0,5fr)] lg:items-end lg:gap-16">
          <div className="min-w-0">
            <TimelineEyebrow>
              <RichTextContent
                path="investorscontent.value_eyebrow"
                label="Value-Add Eyebrow"
                as="span"
                className={`${TIMELINE_EYEBROW_CLASS} text-[rgb(var(--ala-on-primary))]/70`}
              />
            </TimelineEyebrow>
            <RichTextContent
              path="investorscontent.value_headline"
              label="Value-Add Headline"
              as="h2"
              className={`${TIMELINE_H2_CLASS} text-[rgb(var(--ala-on-primary))] [&_em]:not-italic [&_em]:text-[rgb(var(--ala-secondary))]`}
            />
          </div>
          <div className="min-w-0">
            <RichTextContent
              path="investorscontent.value_body"
              label="Value-Add Body"
              as="p"
              className="font-body text-lg leading-relaxed text-[rgb(var(--ala-on-primary))]/75"
            />
          </div>
        </div>
        <RichTextContent
          path="investorscontent.value_levers"
          label="Value-Add Levers"
          as="div"
          className="mt-14 border-t border-[rgb(var(--ala-on-primary))]/10 pt-14 font-body text-base leading-relaxed text-[rgb(var(--ala-on-primary))]/70 md:mt-20 md:pt-16 [&_li]:flex [&_li]:flex-col [&_li]:rounded-2xl [&_li]:border [&_li]:border-[rgb(var(--ala-on-primary))]/10 [&_li]:bg-[rgb(var(--ala-on-primary))]/[0.04] [&_li]:p-7 [&_li]:transition-colors [&_li:hover]:border-[rgb(var(--ala-secondary))]/40 md:[&_li]:p-8 [&_li]:before:mb-6 [&_li]:before:block [&_li]:before:h-1 [&_li]:before:w-10 [&_li]:before:rounded-full [&_li]:before:bg-[rgb(var(--ala-secondary))] [&_strong]:mb-2 [&_strong]:block [&_strong]:font-heading [&_strong]:text-xl [&_strong]:font-semibold [&_strong]:text-[rgb(var(--ala-on-primary))] [&_ul]:grid [&_ul]:list-none [&_ul]:gap-5 md:[&_ul]:grid-cols-3"
        />
      </div>
    </section>
  );
}

function CapitalPartnersSection() {
  const partnersPortal = useSlotGroup('investorscontent.partners_portal', partnersPortalDef);
  const formEmail = useContentToken('investorscontent.form_email');

  return (
    <section id="investor-contact" className="w-full scroll-mt-24 bg-[rgb(var(--ala-surface))] py-20 md:py-28">
      <div className="mx-auto grid max-w-[1440px] gap-10 px-6 md:px-[clamp(16px,3.3vw,48px)] lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)] lg:gap-16">
        <div className="flex flex-col">
          <div className="mb-5 flex items-center gap-2">
            <span className="h-2 w-2 shrink-0 rounded-[3px] bg-[rgb(var(--ala-accent))]" aria-hidden />
            <RichTextContent
              path="investorscontent.partners_eyebrow"
              label="Partners Eyebrow"
              as="span"
              className={`${TIMELINE_EYEBROW_CLASS} text-[rgb(var(--ala-text-muted))]`}
            />
          </div>
          <RichTextContent
            path="investorscontent.partners_headline"
            label="Partners Headline"
            as="h2"
            className={`${TIMELINE_H2_CLASS} text-[rgb(var(--ala-text))]`}
          />
          <RichTextContent
            path="investorscontent.partners_body"
            label="Partners Body"
            as="p"
            className="mt-5 font-body text-lg leading-relaxed text-[rgb(var(--ala-text-muted))]"
          />
          <RichTextContent
            path="investorscontent.partners_list"
            label="Capital Partner Types"
            as="div"
            className="mt-8 font-heading text-lg font-medium text-[rgb(var(--ala-text))] [&_li]:flex [&_li]:items-center [&_li]:gap-4 [&_li]:border-t [&_li]:border-[rgb(var(--ala-border))] [&_li]:py-4 [&_li]:transition-all [&_li]:duration-300 [&_li]:before:h-2 [&_li]:before:w-2 [&_li]:before:shrink-0 [&_li]:before:rounded-full [&_li]:before:bg-[rgb(var(--ala-secondary))] [&_li]:before:transition-all hover:[&_li]:pl-2 hover:[&_li]:text-[rgb(var(--ala-secondary))] [&_ul]:list-none [&_ul]:border-b [&_ul]:border-[rgb(var(--ala-border))]"
          />
          <div
            onMouseMove={trackSpotlight}
            className="group relative mt-10 flex flex-col gap-4 overflow-hidden rounded-3xl bg-[rgb(var(--ala-primary))] p-6 sm:flex-row sm:items-center sm:justify-between md:p-7"
          >
            <Spotlight />
            <RichTextContent
              path="investorscontent.portal_prompt"
              label="Portal Prompt"
              as="span"
              className="relative z-10 font-heading text-lg font-medium text-[rgb(var(--ala-on-primary))]"
            />
            <div className="relative z-10">
              <EditableSlotGroup groupPath="investorscontent.partners_portal" definition={partnersPortalDef}>
                <PortalButton data={partnersPortal} />
              </EditableSlotGroup>
            </div>
          </div>
        </div>

        <div className="rounded-3xl bg-[rgb(var(--ala-background))] p-7 shadow-[0_20px_60px_rgba(10,22,38,0.08)] md:p-10">
          <RichTextContent
            path="investorscontent.form_heading"
            label="Form Heading"
            as="h3"
            className="font-heading text-[clamp(24px,2.2vw,32px)] font-semibold text-[rgb(var(--ala-text))]"
          />
          <RichTextContent
            path="investorscontent.form_intro"
            label="Form Intro"
            as="p"
            className="mb-8 mt-2 font-body text-sm leading-relaxed text-[rgb(var(--ala-text-muted))]"
          />
          <div className="sr-only">
            <EditableToken path="investorscontent.form_email" as="span">
              {formEmail}
            </EditableToken>
          </div>
          <InvestorContactForm formEmailPath="investorscontent.form_email" sectionId="investors-content" />
        </div>
      </div>
    </section>
  );
}

export function InvestorsContent() {
  const editMode = useEditMode();
  const award1 = useSlotGroup('investorscontent.award_1', award1Def);
  const award2 = useSlotGroup('investorscontent.award_2', award2Def);
  const award3 = useSlotGroup('investorscontent.award_3', award3Def);
  const award4 = useSlotGroup('investorscontent.award_4', award4Def);
  const award5 = useSlotGroup('investorscontent.award_5', award5Def);
  const award6 = useSlotGroup('investorscontent.award_6', award6Def);
  const award7 = useSlotGroup('investorscontent.award_7', award7Def);
  const award8 = useSlotGroup('investorscontent.award_8', award8Def);
  const award9 = useSlotGroup('investorscontent.award_9', award9Def);
  const award10 = useSlotGroup('investorscontent.award_10', award10Def);
  const award11 = useSlotGroup('investorscontent.award_11', award11Def);
  const award12 = useSlotGroup('investorscontent.award_12', award12Def);
  const filledAwards = [
    { award: award1 },
    { award: award2, knockoutWhite: true },
    { award: award3 },
    { award: award4, knockoutWhite: true },
    { award: award5 },
    { award: award6 },
    { award: award7 },
    { award: award8 },
    { award: award9 },
    { award: award10 },
    { award: award11 },
    { award: award12 },
  ].filter(
    ({ award }) => award.image,
  );
  const showAward = (award: SlotData) => editMode || Boolean(award.image);
  const teamCta = useSlotGroup('investorscontent.team_cta', teamCtaDef);
  const contactCta = useSlotGroup('investorscontent.contact_cta', contactCtaDef);
  const loginCta = useSlotGroup('investorscontent.login_cta', loginCtaDef);
  const timelinePortal = useSlotGroup('investorscontent.timeline_portal', timelinePortalDef);
  const milestone1 = useSlotGroup('investorscontent.milestone_1', milestone1Def);
  const milestone2 = useSlotGroup('investorscontent.milestone_2', milestone2Def);
  const milestone3 = useSlotGroup('investorscontent.milestone_3', milestone3Def);
  const milestone4 = useSlotGroup('investorscontent.milestone_4', milestone4Def);
  const milestone5 = useSlotGroup('investorscontent.milestone_5', milestone5Def);
  const milestone6 = useSlotGroup('investorscontent.milestone_6', milestone6Def);
  const milestone7 = useSlotGroup('investorscontent.milestone_7', milestone7Def);
  const milestone8 = useSlotGroup('investorscontent.milestone_8', milestone8Def);
  const milestone9 = useSlotGroup('investorscontent.milestone_9', milestone9Def);
  const milestone10 = useSlotGroup('investorscontent.milestone_10', milestone10Def);
  const choosePerson = useSlotGroup('investorscontent.choose_person', choosePersonDef);

  const chooseRef = useRef<HTMLElement>(null);
  const { scrollYProgress: chooseProgress } = useScroll({
    target: chooseRef,
    offset: ['start end', 'end start'],
  });

  return (
    <>
      {/* Why ALA */}
      <section className="w-full bg-[rgb(var(--ala-surface))] py-20 md:py-[clamp(80px,11vw,160px)]">
        <div className="mx-auto max-w-[1440px] px-6 md:px-[clamp(16px,3.3vw,48px)]">
          <div className="hidden">
            <RichTextContent
              path="investorscontent.why_eyebrow"
              label="Why Eyebrow"
              as="span"
            />
            <RichTextContent
              path="investorscontent.why_headline"
              label="Why Headline"
              as="h2"
            />
            <RichTextContent
              path="investorscontent.why_body"
              label="Why Body"
              as="span"
            />
          </div>
          <div className="flex flex-col gap-8 md:gap-10">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="flex flex-col gap-3 md:gap-4"
            >
              <RichTextContent
                path="investorscontent.pillar_1_title"
                label="Pillar 1 Title"
                as="h3"
                className="font-heading text-[clamp(48px,8vw,120px)] font-bold leading-[0.95] tracking-[-0.04em] text-[rgb(var(--ala-dark))]"
              />
              <RichTextContent
                path="investorscontent.pillar_1_body"
                label="Pillar 1 Body"
                as="p"
                className="max-w-[520px] font-heading text-[clamp(18px,1.5vw,22px)] font-normal leading-[1.5] text-[rgb(var(--ala-dark))]/60"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="flex flex-col gap-3 md:items-end md:gap-4 md:text-right"
            >
              <RichTextContent
                path="investorscontent.pillar_2_title"
                label="Pillar 2 Title"
                as="h3"
                className="font-heading text-[clamp(48px,8vw,120px)] font-bold leading-[0.95] tracking-[-0.04em] text-[rgb(var(--ala-dark))]"
              />
              <RichTextContent
                path="investorscontent.pillar_2_body"
                label="Pillar 2 Body"
                as="p"
                className="max-w-[520px] font-heading text-[clamp(18px,1.5vw,22px)] font-normal leading-[1.5] text-[rgb(var(--ala-dark))]/60"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="flex flex-col gap-3 md:gap-4"
            >
              <RichTextContent
                path="investorscontent.pillar_3_title"
                label="Pillar 3 Title"
                as="h3"
                className="font-heading text-[clamp(48px,8vw,120px)] font-bold leading-[0.95] tracking-[-0.04em] text-[rgb(var(--ala-dark))]"
              />
              <RichTextContent
                path="investorscontent.pillar_3_body"
                label="Pillar 3 Body"
                as="p"
                className="max-w-[520px] font-heading text-[clamp(18px,1.5vw,22px)] font-normal leading-[1.5] text-[rgb(var(--ala-dark))]/60"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Awards marquee */}
      {(editMode || filledAwards.length > 0) && (
        <div className="w-full overflow-clip bg-[rgb(var(--ala-surface))] py-16">
          <motion.div
            className="flex w-max shrink-0 items-center gap-10 md:gap-14"
            animate={{ x: ['0%', '-50%'] }}
            transition={{ duration: 45, repeat: Infinity, ease: 'linear' }}
          >
            {showAward(award1) && (
              <EditableSlotGroup groupPath="investorscontent.award_1" definition={award1Def}>
                <AwardLogo image={award1.image} alt={award1.alt} />
              </EditableSlotGroup>
            )}
            {showAward(award2) && (
              <EditableSlotGroup groupPath="investorscontent.award_2" definition={award2Def}>
                <AwardLogo image={award2.image} alt={award2.alt} knockoutWhite />
              </EditableSlotGroup>
            )}
            {showAward(award3) && (
              <EditableSlotGroup groupPath="investorscontent.award_3" definition={award3Def}>
                <AwardLogo image={award3.image} alt={award3.alt} />
              </EditableSlotGroup>
            )}
            {showAward(award4) && (
              <EditableSlotGroup groupPath="investorscontent.award_4" definition={award4Def}>
                <AwardLogo image={award4.image} alt={award4.alt} knockoutWhite />
              </EditableSlotGroup>
            )}
            {showAward(award5) && (
              <EditableSlotGroup groupPath="investorscontent.award_5" definition={award5Def}>
                <AwardLogo image={award5.image} alt={award5.alt} />
              </EditableSlotGroup>
            )}
            {showAward(award6) && (
              <EditableSlotGroup groupPath="investorscontent.award_6" definition={award6Def}>
                <AwardLogo image={award6.image} alt={award6.alt} />
              </EditableSlotGroup>
            )}
            {showAward(award7) && (
              <EditableSlotGroup groupPath="investorscontent.award_7" definition={award7Def}>
                <AwardLogo image={award7.image} alt={award7.alt} />
              </EditableSlotGroup>
            )}
            {showAward(award8) && (
              <EditableSlotGroup groupPath="investorscontent.award_8" definition={award8Def}>
                <AwardLogo image={award8.image} alt={award8.alt} />
              </EditableSlotGroup>
            )}
            {showAward(award9) && (
              <EditableSlotGroup groupPath="investorscontent.award_9" definition={award9Def}>
                <AwardLogo image={award9.image} alt={award9.alt} />
              </EditableSlotGroup>
            )}
            {showAward(award10) && (
              <EditableSlotGroup groupPath="investorscontent.award_10" definition={award10Def}>
                <AwardLogo image={award10.image} alt={award10.alt} />
              </EditableSlotGroup>
            )}
            {showAward(award11) && (
              <EditableSlotGroup groupPath="investorscontent.award_11" definition={award11Def}>
                <AwardLogo image={award11.image} alt={award11.alt} />
              </EditableSlotGroup>
            )}
            {showAward(award12) && (
              <EditableSlotGroup groupPath="investorscontent.award_12" definition={award12Def}>
                <AwardLogo image={award12.image} alt={award12.alt} />
              </EditableSlotGroup>
            )}
            <div className="flex shrink-0 items-center gap-10 pr-10 md:gap-14 md:pr-14" aria-hidden>
              {filledAwards.map(({ award, knockoutWhite }, i) => (
                <AwardLogo
                  key={`${award.image}-${i}`}
                  image={award.image}
                  alt=""
                  knockoutWhite={knockoutWhite}
                />
              ))}
            </div>
          </motion.div>
        </div>
      )}

      {/* Leadership */}
      <section className="w-full bg-[rgb(var(--ala-background))] py-20 md:py-[clamp(80px,11vw,160px)]">
        <div className="mx-auto max-w-[1440px] px-6 text-center md:px-[clamp(16px,3.3vw,48px)]">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-4 flex items-center justify-center gap-2"
          >
            <EyebrowDot />
            <RichTextContent
              path="investorscontent.leaders_eyebrow"
              label="Leaders Eyebrow"
              as="span"
              className="font-heading text-sm font-medium uppercase tracking-[0.12em] text-[rgb(var(--ala-text-muted))]"
            />
          </motion.div>
          <RichTextContent
            path="investorscontent.leaders_headline"
            label="Leaders Headline"
            as="h2"
            className="mx-auto max-w-[720px] font-heading text-[clamp(32px,4vw,56px)] font-medium leading-[1.05] tracking-[-0.03em] text-[rgb(var(--ala-text))]"
          />
          <RichTextContent
            path="investorscontent.leaders_body"
            label="Leaders Body"
            as="p"
            className="mx-auto mt-4 max-w-[640px] font-heading text-base leading-relaxed text-[rgb(var(--ala-text-muted))]"
          />
          <div className="mx-auto mt-12 grid w-full max-w-[1008px] grid-cols-2 gap-3 sm:gap-3.5 lg:grid-cols-4">
            <EditableSlotGroup
              groupPath="investorscontent.leader_1"
              definition={leader1Def}
              className="h-full w-full"
            >
              <LeaderCard groupPath="investorscontent.leader_1" definition={leader1Def} />
            </EditableSlotGroup>
            <EditableSlotGroup
              groupPath="investorscontent.leader_2"
              definition={leader2Def}
              className="h-full w-full"
            >
              <LeaderCard
                groupPath="investorscontent.leader_2"
                definition={leader2Def}
                fillCircleCrop
              />
            </EditableSlotGroup>
            <EditableSlotGroup
              groupPath="investorscontent.leader_3"
              definition={leader3Def}
              className="h-full w-full"
            >
              <LeaderCard groupPath="investorscontent.leader_3" definition={leader3Def} />
            </EditableSlotGroup>
            <EditableSlotGroup
              groupPath="investorscontent.leader_4"
              definition={leader4Def}
              className="h-full w-full"
            >
              <LeaderCard groupPath="investorscontent.leader_4" definition={leader4Def} />
            </EditableSlotGroup>
            <EditableSlotGroup
              groupPath="investorscontent.leader_5"
              definition={leader5Def}
              className="h-full w-full"
            >
              <LeaderCard
                groupPath="investorscontent.leader_5"
                definition={leader5Def}
                fillCircleCrop
              />
            </EditableSlotGroup>
            <EditableSlotGroup
              groupPath="investorscontent.leader_6"
              definition={leader6Def}
              className="h-full w-full"
            >
              <LeaderCard groupPath="investorscontent.leader_6" definition={leader6Def} />
            </EditableSlotGroup>
            <EditableSlotGroup
              groupPath="investorscontent.leader_7"
              definition={leader7Def}
              className="h-full w-full"
            >
              <LeaderCard groupPath="investorscontent.leader_7" definition={leader7Def} />
            </EditableSlotGroup>
            <EditableSlotGroup
              groupPath="investorscontent.leader_8"
              definition={leader8Def}
              className="h-full w-full"
            >
              <LeaderCard groupPath="investorscontent.leader_8" definition={leader8Def} />
            </EditableSlotGroup>
          </div>
          <div className="mt-10 flex justify-center">
            <EditableSlotGroup groupPath="investorscontent.team_cta" definition={teamCtaDef}>
              <a
                href={teamCta.url || '/team'}
                target={teamCta._target || '_self'}
                className="group inline-flex items-center gap-2 font-heading text-sm font-semibold uppercase tracking-wide text-[rgb(var(--ala-text))] transition-colors hover:text-[rgb(var(--ala-secondary))]"
              >
                {teamCta.label || 'Meet the Capital Markets Team'}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </a>
            </EditableSlotGroup>
          </div>
        </div>
      </section>

      {/* 30-year timeline */}
      <section className="w-full bg-[rgb(var(--ala-dark))] py-20 md:py-28">
        <div className="mx-auto max-w-[1440px] px-6 md:px-[clamp(16px,3.3vw,48px)]">
          <div className="mb-14 grid gap-6 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
            <div className="max-w-[760px]">
              <TimelineEyebrow>
                <RichTextContent
                  path="investorscontent.timeline_eyebrow"
                  label="Timeline Eyebrow"
                  as="span"
                  className={`${TIMELINE_EYEBROW_CLASS} text-[rgb(var(--ala-on-primary))]/70`}
                />
              </TimelineEyebrow>
              <RichTextContent
                path="investorscontent.timeline_headline"
                label="Timeline Headline"
                as="h2"
                className={`${TIMELINE_H2_CLASS} text-[rgb(var(--ala-on-primary))]`}
              />
              <RichTextContent
                path="investorscontent.timeline_body"
                label="Timeline Body"
                as="p"
                className="mt-5 font-body text-lg leading-relaxed text-[rgb(var(--ala-on-primary))]/70"
              />
            </div>
            <EditableSlotGroup groupPath="investorscontent.timeline_portal" definition={timelinePortalDef}>
              <TimelinePortalButton data={timelinePortal} />
            </EditableSlotGroup>
          </div>

          <Timeline
            milestones={[
              milestone1,
              milestone2,
              milestone3,
              milestone4,
              milestone5,
              milestone6,
              milestone7,
              milestone8,
              milestone9,
              milestone10,
            ]}
            renderYearButton={(i, button) => {
              if (i === 0)
                return (
                  <EditableSlotGroup key={i} groupPath="investorscontent.milestone_1" definition={milestone1Def}>
                    {button}
                  </EditableSlotGroup>
                );
              if (i === 1)
                return (
                  <EditableSlotGroup key={i} groupPath="investorscontent.milestone_2" definition={milestone2Def}>
                    {button}
                  </EditableSlotGroup>
                );
              if (i === 2)
                return (
                  <EditableSlotGroup key={i} groupPath="investorscontent.milestone_3" definition={milestone3Def}>
                    {button}
                  </EditableSlotGroup>
                );
              if (i === 3)
                return (
                  <EditableSlotGroup key={i} groupPath="investorscontent.milestone_4" definition={milestone4Def}>
                    {button}
                  </EditableSlotGroup>
                );
              if (i === 4)
                return (
                  <EditableSlotGroup key={i} groupPath="investorscontent.milestone_5" definition={milestone5Def}>
                    {button}
                  </EditableSlotGroup>
                );
              if (i === 5)
                return (
                  <EditableSlotGroup key={i} groupPath="investorscontent.milestone_6" definition={milestone6Def}>
                    {button}
                  </EditableSlotGroup>
                );
              if (i === 6)
                return (
                  <EditableSlotGroup key={i} groupPath="investorscontent.milestone_7" definition={milestone7Def}>
                    {button}
                  </EditableSlotGroup>
                );
              if (i === 7)
                return (
                  <EditableSlotGroup key={i} groupPath="investorscontent.milestone_8" definition={milestone8Def}>
                    {button}
                  </EditableSlotGroup>
                );
              if (i === 8)
                return (
                  <EditableSlotGroup key={i} groupPath="investorscontent.milestone_9" definition={milestone9Def}>
                    {button}
                  </EditableSlotGroup>
                );
              return (
                <EditableSlotGroup key={i} groupPath="investorscontent.milestone_10" definition={milestone10Def}>
                  {button}
                </EditableSlotGroup>
              );
            }}
          />
        </div>
      </section>

      <ValueAddSpotlightSection />

      <FootprintOverviewSection />

      <FeaturedProjectsSection />

      {/* Why Choose Us */}
      <section
        ref={chooseRef}
        className="relative w-full min-h-[100svh] overflow-clip bg-[rgb(var(--ala-dark))] py-20 md:py-32 lg:min-h-[1200px]"
      >
        <div className="relative z-10 mx-auto flex h-full max-w-[1440px] flex-col gap-12 px-4 md:px-8 lg:flex-row">
          <div className="flex w-full shrink-0 flex-col gap-8 lg:sticky lg:top-48 lg:h-fit lg:max-w-[400px]">
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-[4px] bg-white" aria-hidden />
              <RichTextContent
                path="investorscontent.choose_eyebrow"
                label="Why Choose Us Eyebrow"
                as="span"
                className="font-heading text-sm font-medium uppercase tracking-[-0.3px] text-white"
              />
            </div>
            <RichTextContent
              path="investorscontent.choose_quote"
              label="Why Choose Us Quote"
              as="h2"
              className="font-heading text-[clamp(28px,2.59vw,42px)] font-medium leading-[1.1] tracking-[-0.0405em] text-white"
            />
            <EditableSlotGroup
              groupPath="investorscontent.choose_person"
              definition={choosePersonDef}
            >
              <div className="mt-4 flex items-center gap-4">
                <div className="h-16 w-16 overflow-clip rounded-full shadow-[inset_0_0_0_2px_rgba(255,255,255,0.20)]">
                  <VibesitesImage
                    url={
                      choosePerson.image ||
                      ''
                    }
                    alt={choosePerson.alt || choosePerson.name || 'Joe Lubeck'}
                    sizes="64px"
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="flex flex-col">
                  <span className="font-heading text-xl font-medium tracking-[-0.4px] text-white">
                    {choosePerson.name || 'Joe Lubeck'}
                  </span>
                  <span className="font-heading text-sm font-medium uppercase tracking-[-0.3px] text-gray-400">
                    {choosePerson.role || 'Founder & Chief Executive Officer'}
                  </span>
                </div>
              </div>
            </EditableSlotGroup>
          </div>

          <div className="relative flex min-h-[420px] w-full flex-grow flex-col gap-10 lg:min-h-[800px] lg:gap-14">
            <ScrollRevealCard
              progress={chooseProgress}
              index={0}
              total={4}
              className={`flex w-full flex-col gap-12 rounded-[4px] border border-white/10 bg-white/5 p-6 backdrop-blur-md lg:w-[345px] ${CHOOSE_CARD_LAYOUT[0]}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex min-h-[60px] w-[60px] items-center justify-center rounded-full bg-white/10">
                  <div
                    className="h-8 w-8 rotate-45 rounded-sm border-2 border-white/50"
                    aria-hidden
                  />
                </div>
                <RichTextContent
                  path="investorscontent.choose_1_num"
                  label="Card 1 Number"
                  as="span"
                  className="font-mono text-sm font-medium uppercase tracking-[-0.3px] text-gray-400"
                />
              </div>
              <div className="flex flex-col gap-4">
                <RichTextContent
                  path="investorscontent.choose_1_title"
                  label="Card 1 Title"
                  as="h3"
                  className="font-heading text-[clamp(18px,1.48vw,24px)] font-medium leading-[1.2] tracking-[-0.029em] text-white"
                />
                <RichTextContent
                  path="investorscontent.choose_1_body"
                  label="Card 1 Body"
                  as="p"
                  className="font-sans text-base font-normal leading-relaxed tracking-[-0.3px] text-gray-300"
                />
              </div>
            </ScrollRevealCard>

            <ScrollRevealCard
              progress={chooseProgress}
              index={1}
              total={4}
              className={`flex w-full flex-col gap-12 rounded-[4px] border border-white/10 bg-white/5 p-6 backdrop-blur-md lg:w-[345px] ${CHOOSE_CARD_LAYOUT[1]}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex min-h-[60px] w-[60px] items-center justify-center rounded-full bg-white/10">
                  <div
                    className="h-8 w-8 rotate-45 rounded-sm border-2 border-white/50"
                    aria-hidden
                  />
                </div>
                <RichTextContent
                  path="investorscontent.choose_2_num"
                  label="Card 2 Number"
                  as="span"
                  className="font-mono text-sm font-medium uppercase tracking-[-0.3px] text-gray-400"
                />
              </div>
              <div className="flex flex-col gap-4">
                <RichTextContent
                  path="investorscontent.choose_2_title"
                  label="Card 2 Title"
                  as="h3"
                  className="font-heading text-[clamp(18px,1.48vw,24px)] font-medium leading-[1.2] tracking-[-0.029em] text-white"
                />
                <RichTextContent
                  path="investorscontent.choose_2_body"
                  label="Card 2 Body"
                  as="p"
                  className="font-sans text-base font-normal leading-relaxed tracking-[-0.3px] text-gray-300"
                />
              </div>
            </ScrollRevealCard>

            <ScrollRevealCard
              progress={chooseProgress}
              index={2}
              total={4}
              className={`flex w-full flex-col gap-12 rounded-[4px] border border-white/10 bg-white/5 p-6 backdrop-blur-md lg:w-[345px] ${CHOOSE_CARD_LAYOUT[2]}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex min-h-[60px] w-[60px] items-center justify-center rounded-full bg-white/10">
                  <div
                    className="h-8 w-8 rotate-45 rounded-sm border-2 border-white/50"
                    aria-hidden
                  />
                </div>
                <RichTextContent
                  path="investorscontent.choose_3_num"
                  label="Card 3 Number"
                  as="span"
                  className="font-mono text-sm font-medium uppercase tracking-[-0.3px] text-gray-400"
                />
              </div>
              <div className="flex flex-col gap-4">
                <RichTextContent
                  path="investorscontent.choose_3_title"
                  label="Card 3 Title"
                  as="h3"
                  className="font-heading text-[clamp(18px,1.48vw,24px)] font-medium leading-[1.2] tracking-[-0.029em] text-white"
                />
                <RichTextContent
                  path="investorscontent.choose_3_body"
                  label="Card 3 Body"
                  as="p"
                  className="font-sans text-base font-normal leading-relaxed tracking-[-0.3px] text-gray-300"
                />
              </div>
            </ScrollRevealCard>

            <ScrollRevealCard
              progress={chooseProgress}
              index={3}
              total={4}
              className={`flex w-full flex-col gap-12 rounded-[4px] border border-white/10 bg-white/5 p-6 backdrop-blur-md lg:w-[345px] ${CHOOSE_CARD_LAYOUT[3]}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex min-h-[60px] w-[60px] items-center justify-center rounded-full bg-white/10">
                  <div
                    className="h-8 w-8 rotate-45 rounded-sm border-2 border-white/50"
                    aria-hidden
                  />
                </div>
                <RichTextContent
                  path="investorscontent.choose_4_num"
                  label="Card 4 Number"
                  as="span"
                  className="font-mono text-sm font-medium uppercase tracking-[-0.3px] text-gray-400"
                />
              </div>
              <div className="flex flex-col gap-4">
                <RichTextContent
                  path="investorscontent.choose_4_title"
                  label="Card 4 Title"
                  as="h3"
                  className="font-heading text-[clamp(18px,1.48vw,24px)] font-medium leading-[1.2] tracking-[-0.029em] text-white"
                />
                <RichTextContent
                  path="investorscontent.choose_4_body"
                  label="Card 4 Body"
                  as="p"
                  className="font-sans text-base font-normal leading-relaxed tracking-[-0.3px] text-gray-300"
                />
              </div>
            </ScrollRevealCard>
          </div>
        </div>
      </section>

      <CapitalPartnersSection />

      {/* CTA */}
      <section className="relative w-full overflow-clip bg-[rgb(var(--ala-dark))] py-20 md:py-[clamp(80px,11vw,160px)]">
        <div className="pointer-events-none absolute inset-0 opacity-[0.06]">
          <div className="absolute left-1/2 top-1/2 h-[600px] w-[600px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[rgb(var(--ala-secondary))] blur-[150px]" />
        </div>
        <div className="relative z-10 mx-auto flex max-w-[1440px] flex-col items-center px-6 text-center md:px-[clamp(16px,3.3vw,48px)]">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="flex flex-col items-center gap-8"
          >
            <div className="flex items-center gap-2">
              <EyebrowDot light />
              <RichTextContent
                path="investorscontent.cta_eyebrow"
                label="CTA Eyebrow"
                as="span"
                className="font-heading text-sm font-medium uppercase tracking-[0.12em] text-white"
              />
            </div>
            <RichTextContent
              path="investorscontent.cta_headline"
              label="CTA Headline"
              as="h2"
              className="max-w-[1000px] font-heading text-[clamp(36px,5vw,80px)] font-medium leading-[1.02] tracking-[-0.03em] text-[rgb(var(--ala-on-primary))]"
            />
            <RichTextContent
              path="investorscontent.cta_body"
              label="CTA Body"
              as="p"
              className="max-w-[640px] font-heading text-[clamp(18px,1.5vw,22px)] font-normal leading-[1.55] text-white"
            />
            <EditableSlotGroup groupPath="investorscontent.contact_cta" definition={contactCtaDef}>
              <a
                href={contactCta.url || '/contact-us'}
                target={contactCta._target || '_self'}
                className="group inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-[rgb(var(--ala-secondary))] px-7 py-[15px] font-heading text-sm font-semibold uppercase tracking-[-0.3px] text-[rgb(var(--ala-on-secondary))] transition-colors hover:bg-[#3a85d0]"
              >
                {contactCta.label || 'Contact Our Team'}
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </a>
            </EditableSlotGroup>
            <div className="mt-8 flex w-full max-w-[640px] flex-col items-center gap-3 border-t border-[rgb(var(--ala-on-primary))]/10 pt-8">
              <span className="font-heading text-sm font-medium uppercase tracking-[0.12em] text-white">
                Already an Investor?
              </span>
              <EditableSlotGroup groupPath="investorscontent.login_cta" definition={loginCtaDef}>
                <a
                  href={loginCta.url || 'https://www.alapts.com/investors'}
                  target={loginCta._target || '_blank'}
                  rel="noopener noreferrer"
                  className="group inline-flex items-center gap-2 font-heading text-base font-semibold tracking-[-0.2px] text-[rgb(var(--ala-on-primary))] transition-colors hover:text-[rgb(var(--ala-secondary))]"
                >
                  {loginCta.label || 'Investor Login'}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </a>
              </EditableSlotGroup>
            </div>
          </motion.div>
        </div>
      </section>

    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'InvestorsContent',
  displayName: 'Investors Content',
  tokens: {
    'investorscontent.why_eyebrow': {
      label: 'Why Eyebrow',
      type: 'richtext',
      defaultValue: '<p>Why American Landmark?</p>',
    },
    'investorscontent.why_headline': {
      label: 'Why Headline',
      type: 'richtext',
      defaultValue:
        '<p>We Know the Markets.<br/>We Know the Asset.<br/>We Know How to Create Value.</p>',
    },
    'investorscontent.why_body': {
      label: 'Why Body',
      type: 'richtext',
      defaultValue:
        '<p>Great investments begin with a clear understanding of where opportunity exists—and the experience to know what to do with it. American Landmark focuses on mid-priced, value-add multifamily communities in high-growth markets across the U.S. Sun Belt. Our vertically integrated platform brings investment, operations, renovation, revenue management, and property management together under one roof. That integration gives us greater visibility into our assets, greater control over execution, and a direct connection between investment decisions and operating performance.</p>',
    },
    'investorscontent.pillar_1_title': {
      label: 'Pillar 1 Title',
      type: 'richtext',
      defaultValue: '<p>Focused</p>',
    },
    'investorscontent.pillar_1_body': {
      label: 'Pillar 1 Body',
      type: 'richtext',
      defaultValue:
        '<p>We invest where demographic and economic fundamentals support long-term rental demand.</p>',
    },
    'investorscontent.pillar_2_title': {
      label: 'Pillar 2 Title',
      type: 'richtext',
      defaultValue: '<p>Integrated</p>',
    },
    'investorscontent.pillar_2_body': {
      label: 'Pillar 2 Body',
      type: 'richtext',
      defaultValue:
        '<p>We own and operate our communities, giving us greater control over the investment lifecycle.</p>',
    },
    'investorscontent.pillar_3_title': {
      label: 'Pillar 3 Title',
      type: 'richtext',
      defaultValue: '<p>Value-Driven</p>',
    },
    'investorscontent.pillar_3_body': {
      label: 'Pillar 3 Body',
      type: 'richtext',
      defaultValue:
        '<p>We pursue opportunities where operational improvements and targeted capital investment can unlock additional value.</p>',
    },
    'investorscontent.leaders_eyebrow': {
      label: 'Leaders Eyebrow',
      type: 'richtext',
      defaultValue: '<p>capital markets</p>',
    },
    'investorscontent.leaders_headline': {
      label: 'Leaders Headline',
      type: 'richtext',
      defaultValue: '<p>Built on Relationships.</p>',
    },
    'investorscontent.leaders_body': {
      label: 'Leaders Body',
      type: 'richtext',
      defaultValue:
        '<p>Our capital markets team partners with institutional and private investors to structure funds, deepen relationships, and align every commitment with long-term value creation.</p>',
    },
    'investorscontent.timeline_eyebrow': {
      label: 'Timeline Eyebrow',
      type: 'richtext',
      defaultValue: '<p>30-Year Track Record</p>',
    },
    'investorscontent.timeline_headline': {
      label: 'Timeline Headline',
      type: 'richtext',
      defaultValue: '<p>Three decades of building communities.</p>',
    },
    'investorscontent.timeline_body': {
      label: 'Timeline Body',
      type: 'richtext',
      defaultValue:
        '<p>Tap through the moments that shaped us — from our first communities to five funds and approximately $7 billion in gross asset value.</p>',
    },
    'investorscontent.choose_eyebrow': {
      label: 'Why Choose Us Eyebrow',
      type: 'richtext',
      defaultValue: '<p>why choose us</p>',
    },
    'investorscontent.choose_quote': {
      label: 'Why Choose Us Quote',
      type: 'richtext',
      defaultValue:
        '<p>"Success isn\'t measured by the number of communities we manage - it\'s measured by the lives we positively impact every day. That\'s the standard we strive for in everything we do."</p>',
    },
    'investorscontent.choose_1_num': {
      label: 'Card 1 Number',
      type: 'richtext',
      defaultValue: '<p>/01</p>',
    },
    'investorscontent.choose_1_title': {
      label: 'Card 1 Title',
      type: 'richtext',
      defaultValue: '<p>Service You Can Count On</p>',
    },
    'investorscontent.choose_1_body': {
      label: 'Card 1 Body',
      type: 'richtext',
      defaultValue:
        '<p>Fast, responsive support when you need it most. Our team is committed to delivering exceptional service and resolving issues quickly so you can enjoy your home with confidence.</p>',
    },
    'investorscontent.choose_2_num': {
      label: 'Card 2 Number',
      type: 'richtext',
      defaultValue: '<p>/02</p>',
    },
    'investorscontent.choose_2_title': {
      label: 'Card 2 Title',
      type: 'richtext',
      defaultValue: '<p>Hassle-Free Home Transfers</p>',
    },
    'investorscontent.choose_2_body': {
      label: 'Card 2 Body',
      type: 'richtext',
      defaultValue:
        '<p>Moving within the American Landmark family is simple. Whether your needs change or you\'re relocating, transferring to another community is designed to be easy and convenient.</p>',
    },
    'investorscontent.choose_3_num': {
      label: 'Card 3 Number',
      type: 'richtext',
      defaultValue: '<p>/03</p>',
    },
    'investorscontent.choose_3_title': {
      label: 'Card 3 Title',
      type: 'richtext',
      defaultValue: '<p>Worry-Free Living</p>',
    },
    'investorscontent.choose_3_body': {
      label: 'Card 3 Body',
      type: 'richtext',
      defaultValue:
        '<p>Peace of mind comes standard. Landmark360® is built around dependable service, clear communication, and a resident-first experience from move-in through every day of your lease.</p>',
    },
    'investorscontent.choose_4_num': {
      label: 'Card 4 Number',
      type: 'richtext',
      defaultValue: '<p>/04</p>',
    },
    'investorscontent.choose_4_title': {
      label: 'Card 4 Title',
      type: 'richtext',
      defaultValue: '<p>A Resident-First Commitment</p>',
    },
    'investorscontent.choose_4_body': {
      label: 'Card 4 Body',
      type: 'richtext',
      defaultValue:
        '<p>More than an apartment—it\'s a promise. Every interaction is guided by our commitment to creating welcoming communities where residents feel valued, supported, and at home.</p>',
    },
    'investorscontent.value_eyebrow': {
      label: 'Value-Add Eyebrow',
      type: 'richtext',
      defaultValue: '<p>Value-Add Spotlight</p>',
    },
    'investorscontent.value_headline': {
      label: 'Value-Add Headline',
      type: 'richtext',
      defaultValue: '<p>Better communities. <em>Stronger performance.</em></p>',
    },
    'investorscontent.value_body': {
      label: 'Value-Add Body',
      type: 'richtext',
      defaultValue:
        '<p>When residents are better served, communities perform better. Our value-add playbook invests in the homes, amenities, and service that residents feel every day — and that shows up in the numbers.</p>',
    },
    'investorscontent.value_levers': {
      label: 'Value-Add Levers',
      type: 'richtext',
      defaultValue:
        '<ul><li><strong>Interior renovations</strong> that modernize kitchens, baths, and finishes.</li><li><strong>Amenity and curb-appeal upgrades</strong> residents are proud to come home to.</li><li><strong>In-house operations</strong> and revenue management that tighten execution.</li></ul>',
    },
    'investorscontent.partners_eyebrow': {
      label: 'Partners Eyebrow',
      type: 'richtext',
      defaultValue: '<p>Capital Partners</p>',
    },
    'investorscontent.partners_headline': {
      label: 'Partners Headline',
      type: 'richtext',
      defaultValue: '<p>Built for long-term partnerships.</p>',
    },
    'investorscontent.partners_body': {
      label: 'Partners Body',
      type: 'richtext',
      defaultValue:
        '<p>Whether you are a current investor or exploring a new relationship, our capital markets team is here to help. We partner with:</p>',
    },
    'investorscontent.partners_list': {
      label: 'Capital Partner Types',
      type: 'richtext',
      defaultValue:
        '<ul><li>Public &amp; Private Pensions</li><li>Multi-Family Offices</li><li>Single-Family Offices</li><li>High-Net-Worth Individuals (HNWIs)</li></ul>',
    },
    'investorscontent.portal_prompt': {
      label: 'Portal Prompt',
      type: 'richtext',
      defaultValue: '<p>Already an investor?</p>',
    },
    'investorscontent.form_heading': {
      label: 'Form Heading',
      type: 'richtext',
      defaultValue: '<p>Connect with Capital Markets</p>',
    },
    'investorscontent.form_intro': {
      label: 'Form Intro',
      type: 'richtext',
      defaultValue:
        '<p>Tell us a bit about you and a member of our investor relations team will follow up personally.</p>',
    },
    'investorscontent.form_email': {
      label: 'Form Fallback Email',
      type: 'email',
      defaultValue: 'info@alapts.com',
    },
    'investorscontent.cta_eyebrow': {
      label: 'CTA Eyebrow',
      type: 'richtext',
      defaultValue: "<p>Let's Talk</p>",
    },
    'investorscontent.cta_headline': {
      label: 'CTA Headline',
      type: 'richtext',
      defaultValue: '<p>Ready to Explore Investment Opportunities?</p>',
    },
    'investorscontent.cta_body': {
      label: 'CTA Body',
      type: 'richtext',
      defaultValue:
        '<p>Connect with our capital markets team to learn how American Landmark creates value through disciplined multifamily investment strategies.</p>',
    },
    'investorscontent.projects_headline': {
      label: 'Featured Projects Headline',
      type: 'richtext',
      defaultValue: '<p>Featured Projects</p>',
    },
    'investorscontent.footprint_eyebrow': {
      label: 'Footprint Eyebrow',
      type: 'richtext',
      defaultValue: '<p>Footprint Overview</p>',
    },
    'investorscontent.footprint_headline': {
      label: 'Footprint Headline',
      type: 'richtext',
      defaultValue: '<p>Boots on the ground across the Sun Belt.</p>',
    },
    'investorscontent.footprint_body': {
      label: 'Footprint Body',
      type: 'richtext',
      defaultValue:
        '<p>Our teams live and work in the markets we invest in. That presence gives us hyperlocal submarket intelligence — and the ability to act on it quickly.</p>',
    },
    'investorscontent.map_hint': {
      label: 'Map Hint',
      type: 'richtext',
      defaultValue: '<p>Select a state · hover a dot to explore a community</p>',
    },
  },
  slotGroups: {
    'investorscontent.leader_1': leader1Def,
    'investorscontent.leader_2': leader2Def,
    'investorscontent.leader_3': leader3Def,
    'investorscontent.leader_4': leader4Def,
    'investorscontent.leader_5': leader5Def,
    'investorscontent.leader_6': leader6Def,
    'investorscontent.leader_7': leader7Def,
    'investorscontent.leader_8': leader8Def,
    'investorscontent.team_cta': teamCtaDef,
    'investorscontent.timeline_portal': timelinePortalDef,
    'investorscontent.partners_portal': partnersPortalDef,
    'investorscontent.milestone_1': milestone1Def,
    'investorscontent.milestone_2': milestone2Def,
    'investorscontent.milestone_3': milestone3Def,
    'investorscontent.milestone_4': milestone4Def,
    'investorscontent.milestone_5': milestone5Def,
    'investorscontent.milestone_6': milestone6Def,
    'investorscontent.milestone_7': milestone7Def,
    'investorscontent.milestone_8': milestone8Def,
    'investorscontent.milestone_9': milestone9Def,
    'investorscontent.milestone_10': milestone10Def,
    'investorscontent.choose_person': choosePersonDef,
    'investorscontent.award_1': award1Def,
    'investorscontent.award_2': award2Def,
    'investorscontent.award_3': award3Def,
    'investorscontent.award_4': award4Def,
    'investorscontent.award_5': award5Def,
    'investorscontent.award_6': award6Def,
    'investorscontent.award_7': award7Def,
    'investorscontent.award_8': award8Def,
    'investorscontent.award_9': award9Def,
    'investorscontent.award_10': award10Def,
    'investorscontent.award_11': award11Def,
    'investorscontent.award_12': award12Def,
    'investorscontent.contact_cta': contactCtaDef,
    'investorscontent.login_cta': loginCtaDef,
    'investorscontent.projects_services_cta': projectsServicesCtaDef,
    'investorscontent.projects_about_cta': projectsAboutCtaDef,
    'investorscontent.project_1': project1Def,
    'investorscontent.project_2': project2Def,
    'investorscontent.project_3': project3Def,
    'investorscontent.project_4': project4Def,
    'investorscontent.project_5': project5Def,
    'investorscontent.state_1': state1Def,
    'investorscontent.state_2': state2Def,
    'investorscontent.state_3': state3Def,
    'investorscontent.state_4': state4Def,
    'investorscontent.state_5': state5Def,
    'investorscontent.state_6': state6Def,
    'investorscontent.state_7': state7Def,
    'investorscontent.state_8': state8Def,
  },
};

(InvestorsContent as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default InvestorsContent;
